import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { acceptInvite } from "../lib/api";
import { routes } from "../lib/routes";
import { useAuth } from "../hooks/useAuth";
import { BlobDecoration } from "../components/BlobDecoration";
import { Button } from "../components/ui/button";
import { Icon } from "../components/ui/icons/icon";
import nomaIcon from "../assets/noma-icon.svg";

type AcceptState = "loading" | "success" | "error";

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<AcceptState>("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      navigate(`${routes.to.auth.signIn()}?returnUrl=${returnUrl}`, { replace: true });
      return;
    }

    async function accept() {
      if (!token) {
        setError("Invalid invite link");
        setState("error");
        return;
      }

      try {
        const { mapId } = await acceptInvite(token);
        setState("success");
        navigate(routes.to.maps.view(mapId), { replace: true });
      } catch (err) {
        const message = getErrorMessage(err);
        setError(message);
        setState("error");
      }
    }

    accept();
  }, [token, user, authLoading, navigate]);

  const isLoading = state === "loading" || authLoading;

  return (
    <div className="min-h-screen bg-background overflow-hidden relative flex flex-col">
      {/* Decorative blobs */}
      <div className="hidden md:block">
        <BlobDecoration
          className="absolute -top-20 right-0 w-80 h-80 opacity-25 translate-x-1/3 text-chart-1"
          variant={1}
        />
        <BlobDecoration className="absolute top-40 right-0 w-48 h-48 opacity-15 text-chart-2" variant={2} />
        <BlobDecoration className="absolute -bottom-32 -left-40 w-80 h-80 opacity-30 text-chart-5" variant={3} />
        <BlobDecoration className="absolute bottom-20 -left-20 w-40 h-40 opacity-25 text-[#f9b4ab]" variant={4} />
      </div>
      <div className="md:hidden">
        <BlobDecoration className="absolute -top-10 -right-10 w-48 h-48 opacity-20 text-chart-1" variant={1} />
        <BlobDecoration className="absolute top-1/4 -right-20 w-32 h-32 opacity-15 text-chart-2" variant={2} />
        <BlobDecoration className="absolute bottom-32 -left-16 w-56 h-56 opacity-30 text-[#f9b4ab]" variant={3} />
      </div>

      {/* Header */}
      <header className="relative px-6 md:px-8 py-6 flex justify-between items-center max-w-7xl mx-auto w-full">
        <Link to={routes.to.home()} className="hover:opacity-80 transition-opacity">
          <img src={nomaIcon} alt="noma.to" className="h-18 border-3 border-secondary rounded-sm" />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-8 relative">
        <div className="text-center max-w-md">
          <div className="mb-8 inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary">
            <Icon i={state === "error" ? "compass" : "users"} className="w-10 h-10 text-primary-foreground" />
          </div>

          {isLoading && (
            <>
              <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4 tracking-tight">
                Accepting invite...
              </h1>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Please wait a moment.</p>
            </>
          )}

          {state === "success" && (
            <>
              <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4 tracking-tight">Invite accepted!</h1>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Redirecting to the map...</p>
            </>
          )}

          {state === "error" && (
            <>
              <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4 tracking-tight">
                Couldn&apos;t join
              </h1>
              <p className="text-muted-foreground text-base md:text-lg mb-10 leading-relaxed">{error}</p>
              <Button asChild variant="outline" className="rounded-full px-6">
                <Link to={routes.to.home()}>
                  <span>Go to home</span>
                  <Icon i="arrow-right" className="w-4 h-4" />
                </Link>
              </Button>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative py-12 md:py-20 px-6 md:px-8">
        <div className="hidden md:block">
          <BlobDecoration className="absolute -bottom-16 -right-16 w-72 h-72 opacity-20 text-accent" variant={4} />
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-sm text-muted-foreground">
            Create your own adventure map at{" "}
            <Link to={routes.to.home()} className="hover:text-primary transition-colors">
              noma.to
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

function getErrorMessage(err: unknown): string {
  const apiMessage = (
    err as { response?: { data?: { error?: { message?: string } } } }
  )?.response?.data?.error?.message?.toLowerCase();

  const message = apiMessage || (err instanceof Error ? err.message.toLowerCase() : "");

  if (message.includes("not found") || message.includes("invalid")) {
    return "This invite link is invalid or has already been used.";
  }
  if (message.includes("expired")) {
    return "This invite has expired. Please ask the map owner to send a new one.";
  }
  if (message.includes("different email")) {
    return "This invite was sent to a different email address. Please sign in with the correct account.";
  }

  return "Something went wrong. Please try again or ask for a new invite.";
}
